import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

import { useToast } from '@dc-shared-web/components/shadcn/use-toast'

type ResendInvitationParams = {
  id: string
  email: string
}

type ResendInvitationResponse = {
  status: 'error' | 'success'
  message?: string
  error?: string
}

const resendInvitation = async (
  apiClient: AxiosInstance,
  params: ResendInvitationParams
): Promise<ResendInvitationResponse> => {
  const { data } = await apiClient.postForm(
    `/ajax/invite/resend/${params.id}`,
    {
      id: params.id,
    }
  )

  return data.data
}

export const useResendInvitation = (onSuccess?: () => void) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: ResendInvitationParams) =>
      resendInvitation(apiClient, mutationParams),
    {
      onSuccess: (_, successParams) => {
        queryClient.invalidateQueries({
          // When an invitation is resent a new record is created. We don't
          // know where that record will be created in accordance with the
          // current sort selection, so we need to invalidate all queries
          queryKey: ['invitations'],
        })
        toast({
          variant: 'success',
          title: `An invitation has been resent to ${successParams.email}.`,
        })
        onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error resending invitation',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
